import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import {
  EduPlatformApiError,
  fetchPlatformSchool,
  resetPlatformSchoolSetup,
  syncPlatformSchoolIam,
  type PlatformSchoolDetail,
} from "@/api/edu-platform-client";
import {
  canManagePlatformSchools,
  canReadPlatformSchools,
} from "@/navigation/platform-access";
import { routes } from "@/router/all_routes";

export function PlatformSchoolDetailPage() {
  const { slug = "" } = useParams();
  const { user } = useAuth();
  const [school, setSchool] = useState<PlatformSchoolDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canManage = canManagePlatformSchools(user);

  if (!canReadPlatformSchools(user)) {
    return <Navigate to={routes.accessDenied} replace />;
  }

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    fetchPlatformSchool(slug)
      .then((row) => {
        if (!cancelled) {
          setSchool(row);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof EduPlatformApiError ? err.message : "Chargement impossible.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function runSync(force: boolean) {
    if (!slug || !canManage) return;
    setBusy(true);
    setActionMessage(null);
    try {
      const result = await syncPlatformSchoolIam(slug, force);
      setActionMessage(
        `Sync IAM — tuteurs ${result.guardians.succeeded}/${result.guardians.processed}, élèves ${result.students.succeeded}/${result.students.processed}.`,
      );
      const refreshed = await fetchPlatformSchool(slug);
      setSchool(refreshed);
    } catch (err) {
      setActionMessage(err instanceof EduPlatformApiError ? err.message : "Sync échouée.");
    } finally {
      setBusy(false);
    }
  }

  async function runResetSetup() {
    if (!slug || !canManage) return;
    if (!window.confirm("Réinitialiser la mise en route de cette école ?")) return;
    setBusy(true);
    setActionMessage(null);
    try {
      const refreshed = await resetPlatformSchoolSetup(slug);
      setSchool((prev) => (prev ? { ...prev, ...refreshed } : prev));
      setActionMessage("Mise en route réinitialisée.");
    } catch (err) {
      setActionMessage(err instanceof EduPlatformApiError ? err.message : "Action échouée.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="suite-platform-page">
      <header className="suite-platform-page__header">
        <div>
          <Link to={routes.platformSchools} className="small text-decoration-none">
            ← Écoles
          </Link>
          <h1 className="h4 mb-1 mt-2">{school?.organizationName ?? slug}</h1>
          <p className="text-muted small mb-0">{slug}</p>
        </div>
      </header>

      {loading ? <p className="text-muted">Chargement…</p> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {actionMessage ? <div className="alert alert-info">{actionMessage}</div> : null}

      {school ? (
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h6">Effectifs</h2>
                <ul className="list-unstyled mb-0 small">
                  <li>Élèves : {school.studentCount}</li>
                  <li>Tuteurs : {school.guardianCount}</li>
                  <li>Classes : {school.classroomCount}</li>
                  <li>Pays : {school.countryCode ?? "—"}</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h6">Abonnement</h2>
                <ul className="list-unstyled mb-0 small">
                  <li>Plan : {school.platformPlanDisplayName ?? school.platformPlan ?? "—"}</li>
                  <li>
                    Quota : {school.studentCount}
                    {school.maxStudents != null ? ` / ${school.maxStudents}` : ""} élèves
                  </li>
                  <li>Facturation : {school.platformBillingStatus}</li>
                  <li>
                    Setup : {school.setupCompleted ? "Complet" : `Étape ${school.setupCurrentStep}`}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h2 className="h6">Synchronisation IAM</h2>
                <p className="small text-muted mb-3">
                  Tuteurs en attente : {school.pendingGuardianSync} · Élèves en attente :{" "}
                  {school.pendingStudentSync}
                </p>
                {canManage ? (
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={busy}
                      onClick={() => void runSync(false)}
                    >
                      Sync IAM (manquants)
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={busy}
                      onClick={() => void runSync(true)}
                    >
                      Sync IAM (forcer)
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-warning"
                      disabled={busy}
                      onClick={() => void runResetSetup()}
                    >
                      Réinitialiser setup
                    </button>
                  </div>
                ) : (
                  <p className="small text-muted mb-0">
                    Permission <code>edu.platform.tenants.manage</code> requise pour les actions.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
